/**
 * The public `window.ProChat(...)` command dispatcher.
 *
 * This is the piece the Subiz pattern depends on, and the reason the embed
 * tag can be one short line while still offering a usable API.
 *
 * The loader (tier 1) installs `window.ProChat` as a *function* that pushes
 * its arguments into a queue, because the owner's page runs before the widget
 * bundle exists. An owner writing this in their `<head>`:
 *
 *   <script src="…/widget.js" async></script>
 *   <script>ProChat('track', 'cta_click', { id: 'hero' })</script>
 *
 * must not have that second line throw. So the queue is the contract: calls
 * land in `ProChat.q`, and this module drains it once the bundle boots.
 *
 * The bug this fixes: `main.ts` used to assign `window.ProChat = { init }`,
 * overwriting the loader's callable stub with a plain object. The queue was
 * then never drained — `ProChat('show')` silently did nothing, and
 * `ProChat('track', …)` threw `TypeError: ProChat is not a function`. Both
 * are the normal case, not an edge case: that is exactly what the queue
 * exists for.
 */

import { resolveAnonymousId } from './popup-frequency'

/** The callable stub the loader installs, before this module takes over. */
type LoaderStub = ((...args: unknown[]) => void) & {
  q?: unknown[][]
  init: (opts?: { appKey?: string; apiBaseUrl?: string }) => void
}

export type ProChatCommand = 'init' | 'show' | 'open' | 'hide' | 'close' | 'destroy' | 'track'

export interface WidgetHandle {
  show(): void
  hide(): void
  destroy(): void
}

export interface DispatcherOptions {
  /** Defaults for `init` — the snippet only carries an appKey. */
  appKey: string
  apiBaseUrl: string
  /**
   * Resolve the live widget. Null until `init` has run, and again after
   * `destroy`, which is how `show`/`hide` decide whether there is anything
   * to act on.
   */
  getHandle(): WidgetHandle | null
  /** Boot the widget. Called by `ProChat('init', …)` and by auto-init. */
  init(opts: { appKey: string; apiBaseUrl: string }): void
  fetchImpl?: typeof fetch
}

/**
 * Commands whose first argument is a name, not an options object. `open` is
 * an alias of `show` so an owner copying a snippet from another vendor does
 * not get a silent no-op.
 */
const SHOW_ALIASES = new Set(['show', 'open'])

/**
 * Accepts either `'cta_click'` or an object with a `type`, so an owner can
 * send the shape their analytics tooling already emits.
 */
function eventNameOf(arg: unknown): string | null {
  if (typeof arg === 'string') return arg
  if (arg && typeof arg === 'object') {
    const type = (arg as { type?: unknown }).type
    if (typeof type === 'string') return type
  }
  return null
}

/**
 * Install the dispatcher, draining whatever the loader already queued.
 *
 * Returns the dispatcher so callers can hold a reference; it is also left on
 * `window.ProChat` for the owner's page to call.
 */
export function installDispatcher(opts: DispatcherOptions): LoaderStub {
  const w = window as unknown as { ProChat?: LoaderStub }
  const queued = w.ProChat?.q ?? []

  // Post one owner event. Fire-and-forget by design: an analytics call the
  // customer wrote must never be able to throw inside their own click
  // handler or stall their page.
  function track(arg: unknown, payload?: unknown): void {
    const event = eventNameOf(arg)
    if (!event) return
    const appKey = opts.appKey
    if (!appKey) return
    const body = {
      appKey,
      event,
      anonymousId: resolveAnonymousId(appKey),
      payload:
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? (payload as Record<string, unknown>)
          : {},
      page: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer || undefined,
        userAgent: navigator.userAgent,
        device: navigator.maxTouchPoints > 0 ? 'mobile' : 'desktop',
      },
    }
    try {
      void Promise.resolve(
        (opts.fetchImpl ?? fetch)(`${opts.apiBaseUrl.replace(/\/$/, '')}/v1/widget/events`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      ).catch(() => {
        // A dropped analytics event is never worth surfacing.
      })
    } catch {
      // Synchronous throw (bad fetchImpl, detached document) — swallow it
      // for the same reason.
    }
  }

  function run(cmd: string, rest: unknown[]): void {
    if (SHOW_ALIASES.has(cmd)) {
      opts.getHandle()?.show()
      return
    }
    if (cmd === 'hide' || cmd === 'close') {
      opts.getHandle()?.hide()
      return
    }
    if (cmd === 'destroy') {
      opts.getHandle()?.destroy()
      return
    }
    if (cmd === 'track') {
      track(rest[0], rest[1])
      return
    }
    if (cmd === 'init') {
      // Owner-supplied config wins where present; the snippet's appKey is the
      // fallback so `ProChat('init')` with no args still boots.
      const override = (rest[0] ?? {}) as Partial<{ appKey: string; apiBaseUrl: string }>
      opts.init({
        appKey: override.appKey ?? opts.appKey,
        apiBaseUrl: override.apiBaseUrl ?? opts.apiBaseUrl,
      })
    }
    // Unknown command: ignore. A typo in the customer's page must not throw.
  }

  const dispatcher = ((...args: unknown[]) => {
    run(String(args[0] ?? ''), args.slice(1))
  }) as LoaderStub

  /**
   * Back-compat: before the command dispatcher, `ProChat.init({ appKey })`
   * was the only public entry point and existing embeds call it that way.
   * Kept working, so upgrading the widget does not break a page that has
   * been embedding it for months.
   */
  dispatcher.init = (opts?: { appKey?: string; apiBaseUrl?: string }) => {
    run('init', [opts ?? {}])
  }

  // Keep `.q` pointing at a live array so an owner inspecting it (or a second
  // copy of the snippet pasted later) still sees a coherent object.
  dispatcher.q = []

  // Drain after the dispatcher is installed, so a queued `show` sees a
  // handle that already exists rather than racing `init`.
  //
  // Auto-init only when an appKey is known. Without one there is nothing to
  // mount — but the dispatcher is still installed, because `ProChat.init()`
  // may be called later by the page itself.
  if (opts.appKey) {
    opts.init({ appKey: opts.appKey, apiBaseUrl: opts.apiBaseUrl })
  }
  for (const call of queued) {
    // `Array.isArray` is the wrong test here. The inline snippet queues
    // `arguments`, not a real array — so every command the owner's page fired
    // before the bundle loaded failed this check and was dropped in silence.
    // `Array.isArray(arguments)` is false; the object is array-*like*, which
    // is all the drain needs.
    if (!call || typeof call !== 'object') continue
    const args = Array.prototype.slice.call(call) as unknown[]
    if (args.length === 0) continue
    run(String(args[0]), args.slice(1))
  }
  queued.length = 0

  w.ProChat = dispatcher
  return dispatcher
}